import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import ContactSection from "@/components/sections/ContactSection";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import useSeo from "@/hooks/useSeo";
import { Quote, MapPin } from "lucide-react";

export default function UeberUns() {
  const { t } = useLang();
  const [stats, setStats] = useState(null);

  useSeo({
    title: "Über uns · DNA Automotive GmbH in der MotorWorld Köln-Rheinland",
    description: "Lernen Sie DNA Automotive kennen: Geschäftsführer Tim Tiedemann und sein Team in der MotorWorld Köln-Rheinland. Leidenschaft für exklusive Fahrzeuge, Transparenz und Premium-Service.",
    path: "/ueber-uns",
    breadcrumbs: [{ name: "Über uns", url: "/ueber-uns" }],
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const statsList = (t.about.stats || []).map((s, i) => {
    if (!stats) return s;
    if (i === 0) return { ...s, value: `${stats.vehicles_total}+` };
    if (i === 1) return { ...s, value: `${stats.brands}` };
    return s;
  });

  return (
    <div className="bg-obsidian min-h-screen" data-testid="ueber-uns-page">
      <Header />

      {/* Gründer prominent oben */}
      <section className="relative pt-36 pb-20 px-6 md:px-10 overflow-hidden grain" data-testid="ueber-uns-hero">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6 order-2 lg:order-1"
          >
            <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-4">{t.about.overline}</p>
            <h1 className="font-heading font-black uppercase tracking-tighter text-white text-4xl md:text-6xl leading-[0.95] mb-6">
              {t.about.title}
            </h1>
            <p className="text-zinc-400 leading-relaxed mb-5 max-w-2xl">{t.about.text}</p>
            <p className="text-zinc-400 leading-relaxed mb-8 max-w-2xl">{t.about.text2}</p>
            <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest">
              <MapPin className="w-4 h-4 text-brand-bright" />
              {t.about.locationCaption}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="lg:col-span-6 order-1 lg:order-2 relative overflow-hidden border border-white/10 max-w-xl w-full mx-auto lg:mx-0 lg:justify-self-end"
            data-testid="ueber-uns-founder"
          >
            <img
              src="/team/tim.png"
              alt={t.about.founderName}
              className="w-full h-full object-cover aspect-[4/5]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6">
              <div className="font-heading font-black uppercase tracking-tight text-white text-3xl">{t.about.founderName}</div>
              <div className="text-brand-bright text-xs uppercase tracking-[0.25em] mt-1">{t.about.founderRole}</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Zitat */}
      <section className="px-6 md:px-10 pb-20">
        <div className="max-w-[1600px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative border-l-2 border-brand-light pl-6 md:pl-10 max-w-3xl"
            data-testid="ueber-uns-quote"
          >
            <Quote className="w-8 h-8 text-brand-bright/40 mb-3" />
            <p className="font-heading text-white text-2xl md:text-4xl leading-snug italic">{t.about.quote}</p>
          </motion.div>
        </div>
      </section>

      {/* Dynamische Kennzahlen */}
      <section className="px-6 md:px-10 pb-24">
        <div className="max-w-[1600px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="ueber-uns-stats">
          {statsList.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-obsidian p-6 md:p-8"
              data-testid={`ueber-uns-stat-${i}`}
            >
              <div className="font-heading font-black text-brand-bright text-3xl md:text-4xl">{s.value}</div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mt-2">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      <ContactSection />
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
